import { useMutation } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { printWeeklySchedule } from '@/services/main/visitSchedule';
import type { PrintWeeklySchedulePayload } from '@/services/main/visitSchedule';
import { getApiErrorMessage } from '@/utils/apiError';

/**
 * Mutation hook for printing weekly visit schedule as PDF.
 *
 * - Calls clinic-scoped API and receives a PDF Blob.
 * - Opens PDF in a new tab (attempts to avoid popup blocking by opening the tab synchronously).
 * - Shows a toast on failure.
 */
export function usePrintWeeklyScheduleMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<Blob, Error, PrintWeeklySchedulePayload>(
    {
      mutationFn: payload => printWeeklySchedule(tenant, clinicNumber, payload),
      onError: error => {
        toast.error(getApiErrorMessage(error, '印刷に失敗しました。'));
      },
    }
  );

  const triggerPrint = async (payload: PrintWeeklySchedulePayload) => {
    const tab = window.open('', '_blank');

    try {
      const blob = await mutateAsync(payload);
      const url = URL.createObjectURL(blob);

      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      tab?.close();
    }
  };

  return { triggerPrint, isLoading };
}

